// Pure helpers for the phone floor sale (FashionFloor). No DB, no Core calls.
import { readVariantAxes } from "../../../../utils/fashion/styleIndex";

const squash = (s) => String(s ?? "").toLowerCase().replace(/[\s-]/g, "");

// A typed mobile number as the customer search expects it: digits only, without
// the country code (+91 / 91 on a 12-digit number) or a leading trunk 0.
export function normalizeMobile(raw) {
  let d = String(raw ?? "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d;
}

// What to send to the customer search: a normalised number when the text is a
// phone number ("+91 98765-43210"), else the trimmed text (a name or email).
export function lookupKey(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  return /^[+\d\s()-]+$/.test(text) ? normalizeMobile(text) : text;
}

// A style matches on its name or brand, or a variant's code (with or without
// dashes, any case) or barcode.
export function matchesStyle(style, query) {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return true;
  if ((style.name || "").toLowerCase().includes(q) || (style.brandName || "").toLowerCase().includes(q)) return true;
  const code = squash(q);
  return style.variants.some((v) => (code && squash(v.product?.value).includes(code)) || v.product?.upc === q);
}

// This store's last-synced stock for a variant: { n, text, tone }, or null when
// the till doesn't track it.
export function stockState(variant) {
  const raw = variant?.onhandQty;
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return { n: 0, text: "Not in this store", tone: "warn" };
  if (n === 1) return { n, text: "Last one here", tone: "warn" };
  return { n, text: `${n} here`, tone: "ok" };
}

// Lines on the bill (a zero-quantity line is a removed one), with colour · size.
export function bagLines(cart, productById) {
  return (cart?.items || [])
    .filter((item) => item && Number(item.weight) !== 0)
    .map((item) => {
      const p = productById?.(item.productId);
      const { colour, size } = p?.parentProductId ? readVariantAxes(p) : { colour: null, size: null };
      return { item, variant: [colour, size].filter(Boolean).join(" · ") };
    });
}

export function bagCount(cart) {
  return (cart?.items || []).reduce((n, i) => n + (Number(i?.weight) > 0 ? Number(i.weight) : 0), 0);
}

// Bill totals as Core priced them: discount and tax are already in `total`.
export function billTotals(cart) {
  return {
    discount: Math.abs(Number(cart?.discount) || 0),
    tax: Number(cart?.tax) || 0,
    total: Number(cart?.total) || 0,
  };
}

// Why this bill can't go to the till, or null when it can.
export function sendBlockedReason(cart, selectedReturnType) {
  if (cart?.isReturn === true || selectedReturnType === "EXCHANGE") return "Returns and exchanges are done at the till.";
  if ((cart?.payments || []).length > 0) return "This bill already has a payment. Finish it at the till.";
  if (bagCount(cart) <= 0) return "The bag is empty.";
  return null;
}

export function initials(name) {
  return (
    String(name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}
